import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useWorkflows } from "../hooks/useWorkflows";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useIntegrations } from "../hooks/useIntegrations";
import { SheetTargetField, SheetColumnsEditor } from "../Components/IntegrationConfigEditor";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    samePath, getStepAt, replaceStepAt, removeStepAt, appendStepAt, listLengthAt,
    lanesFor, priorStepsAlongPath, formatStepIndex, NO_NEST_TYPES
} from "../utils/stepTree";
import TagsInput from "../Components/TagsInput";

// ─── Icon helpers (inline SVG, no dep) ─────────────────────────────────────
function Icon({ name, className = "w-4 h-4" }) {
    const icons = {
        tag: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />,
        star: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />,
        mail: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
        send: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />,
        zap: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />,
        bookmark: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />,
        "git-branch": <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 3v12M18 9a3 3 0 100-6 3 3 0 000 6zm-12 6a3 3 0 100 6 3 3 0 000-6zm12-6a9 9 0 01-9 9" />,
        slack: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5zm-5 5c0 .83-.67 1.5-1.5 1.5S6.5 15.83 6.5 15v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5zm5 0v2.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5S16.33 15 15.5 15H14.5zm-10-5H2c-.83 0-1.5.67-1.5 1.5S1.17 13 2 13h2.5M19.5 10H17m2.5-3.5c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v2.5h2.5c.83 0 1.5-.67 1.5-1.5zM9.5 14.5v2.5c0 .83-.67 1.5-1.5 1.5" />,
        plus: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />,
        trash: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />,
        settings: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />,
        check: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />,
        x: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />,
        cpu: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H7a2 2 0 00-2 2v2M9 3h6M9 3v2m6-2h2a2 2 0 012 2v2m0 0V7m0 0v10m0 0v2a2 2 0 01-2 2h-2m0 0H9m6 0v-2M9 21H7a2 2 0 01-2-2v-2m0 0V9M5 9H3m2 0h14m0 0h2m-2 0v6M5 9v6m0 0H3m2 0h14m0 0h2" />,
        code: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />,
        layers: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />,
        clock: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
        "user-check": <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h11.5M16 19l2 2 4-4" />,
        plug: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v2m-3-3l1.41 1.41M7 12H5m2.59 2.59L6 16m6 0v2m3.59-2.59L17 17M19 12h2m-4.41-4.59L16 6M9 9h6v6H9z" />,
    };
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icons[name] || null}
        </svg>
    );
}

const STEP_COLORS = {
    "ai.classify":    "bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700",
    "ai.score":       "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
    "ai.draft_email": "bg-indigo-100 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700",
    "ai.decide":      "bg-violet-100 dark:bg-violet-900/30 border-violet-300 dark:border-violet-700",
    "ai.extract":     "bg-sky-100 dark:bg-sky-900/30 border-sky-300 dark:border-sky-700",
    "notify.slack":   "bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700",
    "notify.email":   "bg-teal-100 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700",
    "webhook.post":   "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
    "tag.apply":      "bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700",
    "if":             "bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700",
    "parallel":       "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
    "wait":           "bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600",
    "human_approval": "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
};

const TRIGGER_LABELS = {
    on_submit: "On every submission (before AI analysis)",
    on_analysis_complete: "After AI analysis (every submission)",
    on_high_score: "When lead score ≥ threshold",
    on_keyword: "When answer contains keyword",
    on_custom_condition: "Custom condition"
};



// One-line "what is this step actually set to" for the canvas card.
function summarizeStep(step, meta) {
    const c = step.config || {};
    const list = v => (Array.isArray(v) ? v : []).join(" · ");
    const opLabel = (op) => {
        const opt = (meta?.configSchema || []).find(f => f.key === "operator")?.options || [];
        const found = opt.find(o => (o?.value ?? o) === op);
        return (found && found.label) || op || "";
    };
    switch (step.type) {
        case "ai.classify":    return list(c.categories);
        case "tag.apply":      return list(c.tags);
        case "ai.decide":      return list(c.branches);
        case "if":             return c.field ? `${c.field} ${opLabel(c.operator)} ${c.value ?? ""}`.trim() : "";
        case "notify.email":   return c.to || "";
        case "notify.slack":   return c.username || "";
        case "webhook.post":   return c.url || "";
        case "integration.run":return c.action || "";
        case "wait":           return c.duration_seconds ? `${c.duration_seconds}s` : "";
        default:               return "";
    }
}

function StepCard({ step, index, isSelected, onSelect, onDelete, stepMeta }) {
    const colorClass = STEP_COLORS[step.type] || "bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600";
    const meta = stepMeta?.[step.type];
    const summary = summarizeStep(step, meta);
    const needsSetup = (meta?.configSchema || []).some(
        f => f.required && f.key !== "payload" && isBlank((step.config || {})[f.key])
    );
    return (
        <div
            onClick={onSelect}
            className={`border-2 rounded-xl p-3 cursor-pointer transition-all select-none ${colorClass} ${isSelected ? "ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-[#181d33]" : "hover:ring-1 hover:ring-indigo-400"}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400 w-5 flex-shrink-0">#{index + 1}</span>
                    <Icon name={meta?.icon || "zap"} className="w-4 h-4 flex-shrink-0 text-gray-600 dark:text-gray-300" />
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-200 truncate">{step.name || meta?.label || step.type}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{summary || step.type}</p>
                        {needsSetup && (
                            <span className="inline-block mt-1 text-xs font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                                Needs setup
                            </span>
                        )}
                    </div>
                </div>
                <button
                    onClick={e => { e.stopPropagation(); onDelete(); }}
                    className="p-1 rounded text-gray-500 dark:text-gray-400 hover:text-red-500 flex-shrink-0"
                >
                    <Icon name="trash" className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

// ─── Picker for a context path (no {{ }}) — used by "What to classify" and If ─
const ANALYSIS_PATHS = [
    { value: "analysis.leadScore", label: "Lead score (0–100)" },
    { value: "analysis.sentiment", label: "Sentiment" },
    { value: "analysis.summary",   label: "AI summary of the response" },
];

function ContextFieldPicker({ value, onChange, formVars = [], priorSteps = [], emptyLabel, invalid }) {
    const groups = useMemo(() => {
        const answers = formVars.filter(v => v.name).map(v => ({ value: `submission.answers.${v.name}`, label: v.label || v.name }));
        const stepOuts = [];
        for (const s of priorSteps) {
            for (const o of (s.outputs || [])) {
                stepOuts.push({ value: `steps.${s.name}.output.${o.key}`, label: `${s.name} → ${o.key}` });
            }
        }
        return [
            ["This form's answers", answers],
            ["Earlier steps", stepOuts],
            ["AI analysis", ANALYSIS_PATHS],
        ].filter(([, opts]) => opts.length);
    }, [formVars, priorSteps]);

    const allValues = groups.flatMap(([, opts]) => opts.map(o => o.value));
    const [custom, setCustom] = useState(() => !!value && !allValues.includes(value));

    const selectClass = `w-full text-sm border rounded-lg px-3 py-2 bg-[#ffffff] dark:bg-[#181d33] dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${invalid ? "border-red-400 dark:border-red-500" : "dark:border-gray-600"}`;

    return (
        <div>
            <select
                value={custom ? "__custom__" : (value || "")}
                onChange={e => {
                    if (e.target.value === "__custom__") { setCustom(true); return; }
                    setCustom(false);
                    onChange(e.target.value);
                }}
                className={selectClass}
            >
                <option value="">{emptyLabel}</option>
                {groups.map(([label, opts]) => (
                    <optgroup key={label} label={label}>
                        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </optgroup>
                ))}
                <option value="__custom__">Custom path…</option>
            </select>
            {custom && (
                <input
                    type="text"
                    value={value || ""}
                    placeholder="steps.My Step.output.category"
                    onChange={e => onChange(e.target.value)}
                    className={`${selectClass} mt-2 font-mono`}
                />
            )}
            {value && !custom && (
                <p className="text-sm font-mono text-gray-600 dark:text-gray-400 mt-1 truncate">{value}</p>
            )}
            {/* Spell out what the default actually means — "the whole response" is
                abstract until you see your own questions listed. */}
            {!value && !custom && formVars.length > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Reads every answer: {formVars.filter(v => v.name).map(v => v.label).join(" · ")}
                </p>
            )}
        </div>
    );
}

function ConfigField({ field, value, onChange, config = {}, connections = [], channels = [], fieldDefs = [], onCreateSheet, providerSlug, formVars = [], priorSteps = [], invalid = false }) {
    const baseClass = "w-full text-sm border dark:border-gray-600 rounded-lg px-3 py-2 bg-[#ffffff] dark:bg-[#181d33] dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500";

    // Google Sheets: paste/create a sheet, and pick which fields become columns.
    if (field.type === "sheet_target") {
        return (
            <SheetTargetField
                value={value}
                onChange={onChange}
                providerSlug={providerSlug}
                onCreateSheet={onCreateSheet}
                columns={config.payload?.columns}
                fieldDefs={fieldDefs}
                placeholder={field.placeholder || "https://docs.google.com/spreadsheets/d/…"}
            />
        );
    }
    if (field.type === "sheet_columns") {
        return <SheetColumnsEditor value={value} onChange={onChange} fieldDefs={fieldDefs} />;
    }

    // Channel picker — a live dropdown when we have the workspace's channel list (Slack),
    // otherwise a plain text input (#name or ID). Private channels are marked with a lock.
    if (field.type === "channel_picker") {
        if (channels && channels.length) {
            return (
                <select value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass}>
                    <option value="">Select a channel…</option>
                    {channels.map(ch => (
                        <option key={ch.id} value={ch.id}>{ch.is_private ? "🔒 " : "# "}{ch.name}</option>
                    ))}
                </select>
            );
        }
        return (
            <input type="text" value={value || ""} placeholder={field.placeholder || field.help || "#channel-name or channel ID"}
                onChange={e => onChange(e.target.value)} className={baseClass} />
        );
    }

    // Integration connection picker — populated from the user's active integration connections.
    if (field.type === "integration_connection") {
        if (!connections.length) {
            return (
                <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed dark:border-gray-600 rounded-lg px-3 py-2">
                    No connected integrations yet.{" "}
                    <a href="/integrations" target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">Connect one ↗</a>
                </div>
            );
        }
        return (
            <select value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass}>
                <option value="">Select a connection…</option>
                {connections.map(c => (
                    <option key={c.connectionId} value={c.connectionId}>{c.account} ({c.providerSlug})</option>
                ))}
            </select>
        );
    }

    // Integration action picker — scoped to the actions supported by the selected connection.
    if (field.type === "integration_action") {
        const conn = connections.find(c => String(c.connectionId) === String(config.connectionId));
        if (!config.connectionId) {
            return <select disabled className={`${baseClass} opacity-60`}><option>Select a connection first</option></select>;
        }
        const actions = conn?.actions || [];
        return (
            <select value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass}>
                <option value="">Select an action…</option>
                {actions.map(a => (
                    <option key={a.action} value={a.action}>{a.description || a.action}</option>
                ))}
            </select>
        );
    }

    if (field.type === "tags") {
        return (
            <TagsInput
                value={value}
                onChange={onChange}
                placeholder={field.placeholder || "Type a value, then press Enter"}
                examples={field.examples || []}
                invalid={invalid}
            />
        );
    }
    if (field.type === "context_field") {
        return (
            <ContextFieldPicker
                value={value}
                onChange={onChange}
                formVars={formVars}
                priorSteps={priorSteps}
                emptyLabel={field.required ? "Select a value…" : "The whole form response (default)"}
                invalid={invalid}
            />
        );
    }
    // A rubric is just a textarea, but a blank one is intimidating and the AI does
    // far better with rules. Offer a starting point built from THIS form's
    // questions and the categories the user already typed.
    if (field.type === "rubric") {
        const categories = Array.isArray(config.categories) ? config.categories.filter(Boolean) : [];
        const questions = formVars.filter(v => v.name).map(v => v.label);
        const isEmpty = !String(value || "").trim();

        const starter = () => {
            const lines = categories.length
                ? categories.map(c => `${c} = `)
                : ["90-100 = ", "50-89 = ", "0-49 = "];
            if (questions.length) {
                lines.push("", `Base the decision mainly on these answers: ${questions.join(", ")}.`);
            }
            return lines.join("\n");
        };

        return (
            <div>
                <textarea
                    rows={5}
                    value={value || ""}
                    placeholder={field.placeholder}
                    onChange={e => onChange(e.target.value)}
                    className={`${baseClass} resize-y`}
                />
                {isEmpty && (
                    <button
                        type="button"
                        onClick={() => onChange(starter())}
                        className="mt-1 text-xs font-semibold px-2 py-1 rounded border border-dashed border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                    >
                        ✨ Start from this form
                    </button>
                )}
                {questions.length > 0 && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        This form asks: {questions.join(" · ")}
                    </p>
                )}
            </div>
        );
    }
    if (field.type === "textarea" || field.type === "template") {
        return (
            <textarea
                rows={3}
                value={value || ""}
                placeholder={field.placeholder}
                onChange={e => onChange(e.target.value)}
                className={`${baseClass} resize-none font-mono`}
            />
        );
    }
    if (field.type === "json") {
        return (
            <div>
                <textarea
                    rows={4}
                    value={typeof value === "object" ? JSON.stringify(value, null, 2) : (value || "")}
                    placeholder={field.placeholder}
                    onChange={e => {
                        try { onChange(JSON.parse(e.target.value)) }
                        catch { onChange(e.target.value) }
                    }}
                    className={`${baseClass} resize-none font-mono`}
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">JSON — supports {"{{ }}"} templates</p>
            </div>
        );
    }
    if (field.type === "select") {
        // Options are either plain strings or { value, label } pairs.
        return (
            <select value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass}>
                <option value="">Select…</option>
                {(field.options || []).map(o => {
                    const val = o?.value ?? o;
                    return <option key={val} value={val}>{o?.label || val}</option>;
                })}
            </select>
        );
    }
    if (field.type === "number") {
        return (
            <input type="number" value={value ?? ""} placeholder={field.placeholder}
                onChange={e => onChange(Number(e.target.value))} className={baseClass} />
        );
    }
    if (field.type === "password") {
        return (
            <input type="password" value={value || ""} placeholder={field.placeholder || "••••••••"}
                onChange={e => onChange(e.target.value)} className={baseClass} autoComplete="new-password" />
        );
    }
    if (field.type === "checkbox") {
        return (
            <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
                    className="w-4 h-4 accent-indigo-600" />
                <span className="text-gray-600 dark:text-gray-300">{field.label}</span>
            </label>
        );
    }
    if (field.type === "branches_list") {
        // Branch names → stored as string[] in config
        return (
            <div>
                <TagsInput
                    value={value}
                    onChange={onChange}
                    placeholder={field.placeholder || "Type a branch name, then press Enter"}
                    examples={field.examples || []}
                    invalid={invalid}
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Step definitions for each branch are configured after saving.</p>
            </div>
        );
    }
    if (field.type === "json_schema") {
        return (
            <div>
                <textarea
                    rows={6}
                    value={typeof value === "object" ? JSON.stringify(value, null, 2) : (value || "")}
                    placeholder={field.placeholder || '{\n  "type": "object",\n  "properties": {\n    "name": { "type": "string" }\n  },\n  "required": ["name"]\n}'}
                    onChange={e => {
                        try { onChange(JSON.parse(e.target.value)) }
                        catch { onChange(e.target.value) }
                    }}
                    className={`${baseClass} resize-y font-mono`}
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">JSON Schema — defines the structure to extract. <a href="https://json-schema.org/understanding-json-schema/reference/type.html" target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline">Reference ↗</a></p>
            </div>
        );
    }
    if (field.type === "duration") {
        const seconds = Number(value) || 0;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return (
            <div className="flex gap-2 items-center">
                <div className="flex-1">
                    <input type="number" min={0} max={168} value={hours}
                        onChange={e => onChange(Number(e.target.value) * 3600 + minutes * 60 + secs)}
                        className={`${baseClass} text-center`} />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 text-center">hours</p>
                </div>
                <div className="flex-1">
                    <input type="number" min={0} max={59} value={minutes}
                        onChange={e => onChange(hours * 3600 + Number(e.target.value) * 60 + secs)}
                        className={`${baseClass} text-center`} />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 text-center">minutes</p>
                </div>
                <div className="flex-1">
                    <input type="number" min={0} max={59} value={secs}
                        onChange={e => onChange(hours * 3600 + minutes * 60 + Number(e.target.value))}
                        className={`${baseClass} text-center`} />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 text-center">seconds</p>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">{seconds}s total</span>
            </div>
        );
    }
    if (field.type === "parallel_branches") {
        // Simple branch name manager — step definitions per branch done separately
        const branches = typeof value === "object" && !Array.isArray(value) ? value : {};
        const branchNames = Object.keys(branches);
        return (
            <div className="space-y-2">
                {branchNames.map(name => (
                    <div key={name} className="flex items-center gap-2 bg-gray-50 dark:bg-[#181d33] rounded px-2 py-1 border-[1px] dark:border-gray-700">
                        <span className="text-sm font-mono text-indigo-600 dark:text-indigo-400 flex-1">{name}</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{(branches[name] || []).length} step(s)</span>
                        <button onClick={() => {
                            const next = { ...branches };
                            delete next[name];
                            onChange(next);
                        }} className="text-red-400 hover:text-red-600 text-sm">×</button>
                    </div>
                ))}
                <div className="flex gap-2">
                    <input type="text" placeholder="branch name"
                        className={`${baseClass} flex-1`}
                        onKeyDown={e => {
                            if (e.key === "Enter" && e.target.value.trim()) {
                                onChange({ ...branches, [e.target.value.trim()]: [] });
                                e.target.value = "";
                            }
                        }}
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0 self-center">Enter to add</p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Add branch names. Steps for each branch are defined as sub-step arrays in the workflow JSON.</p>
            </div>
        );
    }
    return (
        <input type="text" value={value || ""} placeholder={field.placeholder}
            onChange={e => onChange(e.target.value)} className={baseClass} />
    );
}

const isBlank = (v) =>
    v === undefined || v === null || v === "" ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

function StepConfigPanel({ step, stepMeta, allStepTypes, formVars = [], priorSteps = [], canConvertLegacyIf = false, onConvertLegacyIf, onUpdate, onClose }) {
    const meta = stepMeta?.[step.type] || {};
    const [localConfig, setLocalConfig] = useState(step.config || {});
    const [localName, setLocalName] = useState(step.name || "");
    const [showErrors, setShowErrors] = useState(false);

    // The step can also change from outside the panel — a legacy If getting
    // converted, a workflow reload. Take the new values when that happens.
    useEffect(() => {
        setLocalConfig(step.config || {});
        setLocalName(step.name || "");
    }, [step]);

    // Load the user's integration connections (+ each one's supported actions) so the
    // integration.run step can render real Connection / Action pickers instead of raw text.
    const { listActions, testAction } = useIntegrations();
    const [connections, setConnections] = useState([]);
    const [channels, setChannels] = useState([]);   // live Slack channel list for channel pickers
    useEffect(() => {
        const needsIntegrations = (meta.configSchema || []).some(f => f.type === "integration_connection");
        if (!needsIntegrations) return;
        let alive = true;
        listActions().then(list => { if (alive) setConnections(list || []); }).catch(() => {});
        return () => { alive = false; };
    }, [meta.configSchema, listActions]);

    const selectedConn = connections.find(c => String(c.connectionId) === String(localConfig.connectionId));

    // Google Sheets column picker needs {name,label}; provision-a-sheet button reuses testAction.
    const sheetFieldDefs = useMemo(
        () => (formVars || []).filter(v => v.name).map(v => ({ name: v.name, label: v.label })),
        [formVars]
    );
    const createSheet = useCallback(async (title, headers) => {
        const r = await testAction(localConfig.connectionId, "sheets.create_spreadsheet", { title, headers });
        if (!r?.success) throw new Error(r?.error || "Failed to create sheet");
        return r.output;
    }, [testAction, localConfig.connectionId]);

    // For a Slack connection, fetch the workspace's channels so the channel field is a real
    // dropdown (public + private the bot is in) instead of a raw ID the user has to hunt down.
    useEffect(() => {
        const conn = connections.find(c => String(c.connectionId) === String(localConfig.connectionId));
        if (!conn || conn.providerSlug !== "slack") { setChannels([]); return; }
        let alive = true;
        testAction(conn.connectionId, "slack.list_channels", {})
            .then(r => { if (alive) setChannels(r?.output?.channels || []); })
            .catch(() => { if (alive) setChannels([]); });
        return () => { alive = false; };
    }, [localConfig.connectionId, connections, testAction]);

    // The selected integration action's own parameter schema (Google Sheet,
    // Channel, …). Empty until a connection and action are picked.
    const payloadSchema = useMemo(
        () => (selectedConn?.actions || []).find(a => a.action === localConfig.action)?.configSchema || [],
        [selectedConn, localConfig.action]
    );
    const isPayloadFieldMissing = (pf) => pf.required && isBlank((localConfig.payload || {})[pf.key]);

    // Required fields, checked before save so a misconfigured step fails here
    // instead of at 3am on a real submission. This covers the action's payload
    // parameters too: they used to be skipped entirely, so a Sheets step could be
    // saved with no spreadsheet and would only announce itself as a failed run
    // nobody was watching.
    const missingRequired = [
        ...(meta.configSchema || [])
            .filter(f => f.required && f.key !== "payload" && isBlank(localConfig[f.key]))
            .map(f => f.label),
        ...payloadSchema.filter(isPayloadFieldMissing).map(pf => pf.label),
    ];

    const save = () => {
        if (missingRequired.length) {
            setShowErrors(true);
            toast.error(`Still needed: ${missingRequired.join(", ")}`);
            return;
        }
        setShowErrors(false);
        onUpdate({ ...step, config: localConfig, name: localName || step.type });
        toast("Step saved");
    };

    // Real tokens for the steps that run before this one, so nobody has to guess
    // the shape of `{{ steps.<name>.output.<field> }}`.
    const priorStepVars = useMemo(
        () => priorSteps.flatMap(s => (s.outputs || []).map(o => ({
            token: `{{ steps.${s.name}.output.${o.key} }}`,
            label: `${s.name} → ${o.description || o.key}`
        }))),
        [priorSteps]
    );

    const ANALYSIS_VARS = [
        "{{ analysis.leadScore }}",
        "{{ analysis.sentiment }}",
        "{{ analysis.summary }}",
        "{{ steps.<step_name>.output.<field> }}",
    ];

    const copyVar = (token) => {
        try { navigator.clipboard.writeText(token); toast(`Copied ${token}`); }
        catch { toast("Copy failed — select and copy manually"); }
    };

    const isIntegration = step.type === "integration.run";
    const isLegacyIf = step.type === "if" && !isBlank(localConfig.skipSteps);

    // Legacy-only fields stay hidden unless this step still carries a value for
    // them; `hideWhen` drops fields the current settings make meaningless (a
    // compare value next to "is empty", say).
    const visibleFields = (meta.configSchema || []).filter(f => {
        if (f.legacy && isBlank(localConfig[f.key])) return false;
        if (f.hideWhen && (f.hideWhen.in || []).includes(localConfig[f.hideWhen.key])) return false;
        return true;
    });

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <Icon name={meta.icon || "settings"} className="w-4 h-4 text-indigo-500" />
                    <span className="font-bold text-sm">{meta.label || step.type}</span>
                </div>
                <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Icon name="x" className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* What this step does, in one line — so nobody has to guess from the label. */}
                {(meta.docs || meta.description) && (
                    <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#1e2035] rounded-lg px-3 py-2">
                        {meta.docs || meta.description}
                    </p>
                )}

                {/* Workflows built before if/else still carry skipSteps. Offer the one-click
                    migration instead of leaving two competing behaviours on screen. */}
                {isLegacyIf && (
                    <div className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
                        <p className="mb-2">
                            {Number(localConfig.skipSteps) > 0
                                ? <>This step still uses the old behaviour: skip the next {Number(localConfig.skipSteps)} step{Number(localConfig.skipSteps) === 1 ? "" : "s"} when the check fails.</>
                                : <>This step is set to skip 0 steps, so the check currently changes nothing.</>}
                        </p>
                        {canConvertLegacyIf ? (
                            <button
                                onClick={() => onConvertLegacyIf?.()}
                                className="font-semibold underline hover:no-underline"
                            >
                                Switch to IF YES / IF NO lanes
                            </button>
                        ) : (
                            <p>Clear the skip count below to switch this step over to the IF YES / IF NO lanes.</p>
                        )}
                    </div>
                )}

                {/* Integration steps only fire on real submissions — the Test button is a dry run. */}
                {isIntegration && (
                    <div className="flex gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
                        <span>⚠️</span>
                        <span>This action only runs on <strong>live form submissions</strong>. The <strong>Test</strong> button is a dry run — it validates the setup but won't actually send.</span>
                    </div>
                )}

                {/* Step name */}
                <div>
                    <label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">Step Name</label>
                    <input
                        type="text"
                        value={localName}
                        placeholder={meta.label || step.type}
                        onChange={e => setLocalName(e.target.value)}
                        className="w-full text-sm border-[1px] dark:border-gray-600 rounded-lg px-3 py-2 bg-[#ffffff] dark:bg-[#181d33] dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Used as reference: {"{{ steps.<name>.output.field }}"}  </p>
                </div>

                {/* Dynamic config fields */}
                {visibleFields.map(field => {
                    // For integration.run, render the selected action's parameters as a labeled form
                    // (Channel, Message, …) bound to config.payload — not a raw JSON box.
                    if (field.key === "payload" && step.type === "integration.run") {
                        if (!localConfig.connectionId || !localConfig.action) {
                            return (
                                <div key={field.key}>
                                    <label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">{field.label}</label>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Pick a connection and action above to configure its parameters.</p>
                                </div>
                            );
                        }
                        return (
                            <div key={field.key} className="space-y-3">
                                <label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block">Parameters</label>
                                {payloadSchema.length === 0 && <p className="text-sm text-gray-600 dark:text-gray-400">This action takes no parameters.</p>}
                                {payloadSchema.map(pf => {
                                    const paramMissing = showErrors && isPayloadFieldMissing(pf);
                                    return (
                                        <div key={pf.key}>
                                            <label className="text-sm font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                                                {pf.label}{pf.required && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                            <ConfigField
                                                field={pf}
                                                value={(localConfig.payload || {})[pf.key]}
                                                config={localConfig}
                                                connections={connections}
                                                channels={channels}
                                                fieldDefs={sheetFieldDefs}
                                                providerSlug={selectedConn?.providerSlug}
                                                onCreateSheet={createSheet}
                                                invalid={paramMissing}
                                                onChange={val => setLocalConfig(c => ({ ...c, payload: { ...(c.payload || {}), [pf.key]: val } }))}
                                            />
                                            {pf.help && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pf.help}</p>}
                                            {paramMissing && <p className="text-sm text-red-500 mt-1">This one is required.</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    }

                    const fieldMissing = showErrors && field.required && field.key !== "payload" && isBlank(localConfig[field.key]);

                    return (
                        <div key={field.key}>
                            <label className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-1">
                                {field.label}
                                {field.required
                                    ? <span className="text-red-500 ml-1">*</span>
                                    : <span className="ml-1 normal-case tracking-normal text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>}
                            </label>
                            <ConfigField
                                field={field}
                                value={localConfig[field.key]}
                                config={localConfig}
                                connections={connections}
                                channels={channels}
                                formVars={formVars}
                                priorSteps={priorSteps}
                                invalid={fieldMissing}
                                onChange={val => setLocalConfig(c => {
                                    const next = { ...c, [field.key]: val };
                                    // Changing the connection/action invalidates downstream picks.
                                    if (field.type === "integration_connection") { next.action = ""; next.payload = {}; }
                                    if (field.type === "integration_action") next.payload = {};
                                    return next;
                                })}
                            />
                            {field.help && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{field.help}</p>}
                            {fieldMissing && <p className="text-xs text-red-500 mt-1">This one is required.</p>}
                        </div>
                    );
                })}

                {/* Branch steps are filled in on the canvas, not in this panel. Say so. */}
                {meta.branches && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 border border-dashed dark:border-gray-600 rounded-lg px-3 py-2">
                        {meta.branches.mode === "fixed"
                            ? <>Add the steps for each case on the canvas — under <strong>{(meta.branches.lanes || []).map(l => l.label).join(" and ")}</strong> below this step.</>
                            : <>Every branch name you list above gets its own lane on the canvas. Drop the steps for each one there.</>}
                        <br />
                        Whatever comes after this block runs either way.
                    </div>
                )}

                {/* What this step hands to later steps — the missing half of "how do I use this?" */}
                {(meta.outputs || []).length > 0 && (
                    <div className="border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">This step produces</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Click to copy, then use it in any later step.</p>
                        <div className="space-y-1">
                            {meta.outputs.map(o => {
                                const token = `{{ steps.${localName || step.name || step.type}.output.${o.key} }}`;
                                return (
                                    <button key={o.key} type="button" onClick={() => copyVar(token)} title={`Copy ${token}`}
                                        className="block w-full text-left hover:bg-indigo-100/60 dark:hover:bg-indigo-900/30 rounded px-1 py-0.5">
                                        <span className="block text-xs text-gray-700 dark:text-gray-200">{o.description}</span>
                                        <span className="block text-xs font-mono text-indigo-500 dark:text-indigo-400 truncate">{token}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Template variables — this form's real fields + analysis/steps. Click to copy. */}
                <div className="bg-gray-50 dark:bg-[#23263a] rounded-lg p-3">
                    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Available Variables</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Click to copy, then paste into any text field (e.g. the message).</p>

                    {formVars.length > 0 && (
                        <>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">This form's fields</p>
                            <div className="space-y-0.5 mb-2">
                                {formVars.map(v => (
                                    <button key={v.token} type="button" onClick={() => copyVar(v.token)} title={`Copy ${v.token}`}
                                        className="block w-full text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded px-1 py-0.5">
                                        <span className="block text-xs text-gray-700 dark:text-gray-200 truncate">{v.label}</span>
                                        <span className="block text-xs font-mono text-indigo-500 dark:text-indigo-400 truncate">{v.token}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    {priorStepVars.length > 0 && (
                        <>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Earlier steps</p>
                            <div className="space-y-0.5 mb-2">
                                {priorStepVars.map(v => (
                                    <button key={v.token} type="button" onClick={() => copyVar(v.token)} title={`Copy ${v.token}`}
                                        className="block w-full text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded px-1 py-0.5">
                                        <span className="block text-xs text-gray-700 dark:text-gray-200 truncate">{v.label}</span>
                                        <span className="block text-xs font-mono text-indigo-500 dark:text-indigo-400 truncate">{v.token}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Analysis &amp; steps</p>
                    <div className="space-y-0.5">
                        {ANALYSIS_VARS.map(v => (
                            <button key={v} type="button" onClick={() => copyVar(v)} title={`Copy ${v}`}
                                className="block w-full text-left text-xs font-mono text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded px-1 py-0.5 truncate">
                                {v}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-4 py-3 border-t dark:border-gray-700">
                <button
                    onClick={save}
                    className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
                >
                    Save Step
                </button>
            </div>
        </div>
    );
}

function StepPicker({ stepTypes, onAdd, label = "Add Step", exclude = [] }) {
    const [open, setOpen] = useState(false);
    const groups = {};
    for (const s of stepTypes) {
        if (exclude.includes(s.type)) continue;
        const g = s.type.split(".")[0];
        if (!groups[g]) groups[g] = [];
        groups[g].push(s);
    }
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full border-2 border-dashed border-indigo-400 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400 text-xs font-semibold hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
                <Icon name="plus" className="w-3.5 h-3.5" />
                {label}
            </button>
            {open && (
                <div className="absolute z-50 mt-2 w-64 bg-[#ffffff] dark:bg-[#23263a] border-[1px] dark:border-gray-700 rounded-xl shadow-xl overflow-hidden">
                    <div className="px-3 py-2 border-b dark:border-gray-700">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Choose a step type</p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {Object.entries(groups).map(([group, steps]) => (
                            <div key={group}>
                                <p className="px-3 py-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-[#1e2035]">{group}</p>
                                {steps.map(s => (
                                    <button
                                        key={s.type}
                                        onClick={() => { onAdd(s.type); setOpen(false); }}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-left"
                                    >
                                        <Icon name={s.icon || "zap"} className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{s.label}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{s.description}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


// Renders one list of steps. An If / Else (or AI Decide) step renders its lanes
// underneath itself, each holding its own nested StepList — that indentation is
// the whole point: you can see which steps run in which case.
function StepList({ steps, listPath = [], stepTypes, stepMeta, selectedPath, onSelect, onDelete, onAdd, depth = 0 }) {
    const nested = depth > 0;
    return (
        <div className="space-y-2">
            {steps.map((step, i) => {
                const path = [...listPath, i];
                const meta = stepMeta?.[step.type];
                const lanes = lanesFor(step, meta);
                return (
                    <div key={step.stepId || i}>
                        <StepCard
                            step={step}
                            index={i}
                            isSelected={samePath(selectedPath, path)}
                            onSelect={() => onSelect(samePath(selectedPath, path) ? null : path)}
                            onDelete={() => onDelete(path)}
                            stepMeta={stepMeta}
                        />

                        {lanes.length > 0 && (
                            <div className="mt-2 ml-4 pl-4 border-l-2 border-dashed border-gray-300 dark:border-gray-600 space-y-3">
                                {lanes.map(lane => {
                                    const laneSteps = step.branches?.[lane.key] || [];
                                    return (
                                        <div key={lane.key}>
                                            <div className="flex items-baseline gap-2 mb-1.5">
                                                <span className="text-xs font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                    {lane.label}
                                                </span>
                                                {lane.hint && <span className="text-sm text-gray-600 dark:text-gray-400">{lane.hint}</span>}
                                                {laneSteps.length === 0 && <span className="text-sm text-gray-600 dark:text-gray-400 italic">nothing yet</span>}
                                            </div>
                                            <StepList
                                                steps={laneSteps}
                                                listPath={[...path, lane.key]}
                                                stepTypes={stepTypes}
                                                stepMeta={stepMeta}
                                                selectedPath={selectedPath}
                                                onSelect={onSelect}
                                                onDelete={onDelete}
                                                onAdd={onAdd}
                                                depth={depth + 1}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {i < steps.length - 1 && (
                            <div className="flex justify-center py-1">
                                <div className="w-0.5 h-4 bg-gray-300 dark:bg-gray-600" />
                            </div>
                        )}
                    </div>
                );
            })}

            {steps.length > 0 && !nested && (
                <div className="flex justify-center py-1">
                    <div className="w-0.5 h-3 bg-gray-300 dark:bg-gray-600" />
                </div>
            )}
            <div className={nested ? "flex justify-start pt-1" : "flex justify-center"}>
                <StepPicker
                    stepTypes={stepTypes}
                    onAdd={type => onAdd(listPath, type)}
                    label={nested ? "Add step here" : "Add Step"}
                    exclude={nested ? NO_NEST_TYPES : []}
                />
            </div>
        </div>
    );
}

function RecipeModal({ recipes, onInstall, onClose }) {
    const categories = [...new Set(recipes.map(r => r.category))];
    const [activeCategory, setActiveCategory] = useState("All");
    const filtered = activeCategory === "All" ? recipes : recipes.filter(r => r.category === activeCategory);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-[#ffffff] dark:bg-[#23263a] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
                    <div>
                        <h2 className="text-lg font-bold">Recipe Library</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">One-click workflow templates — install and customise</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                        <Icon name="x" />
                    </button>
                </div>
                <div className="flex gap-2 px-6 pt-3 flex-wrap">
                    {["All", ...categories].map(c => (
                        <button
                            key={c}
                            onClick={() => setActiveCategory(c)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${activeCategory === c ? "bg-indigo-600 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {filtered.map(r => (
                        <div key={r.id} className="border-[1px] dark:border-gray-700 rounded-xl p-4 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors group">
                            <div className="flex items-start gap-3">
                                <span className="text-2xl">{r.icon}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">{r.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.description}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">{r.stepCount} steps</span>
                                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{r.category}</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => onInstall(r.id)}
                                className="mt-3 w-full py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                            >
                                Install Recipe
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function TestModal({ runs: recentResponses, onTest, onClose, loading }) {
    const [selectedId, setSelectedId] = useState(recentResponses[0]?._id || "");
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-[#ffffff] dark:bg-[#23263a] rounded-2xl shadow-2xl w-full max-w-md">
                <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700">
                    <h3 className="font-bold text-sm">Test Workflow</h3>
                    <button onClick={onClose}><Icon name="x" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Select a past submission to dry-run this workflow against.</p>
                    <div className="flex gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
                        <span>⚠️</span>
                        <span>This is a <strong>dry run</strong> — it validates each step but does <strong>not</strong> send messages or run integration actions. To see a real Slack/email/etc. send, submit the form for real with the workflow enabled.</span>
                    </div>
                    <select
                        value={selectedId}
                        onChange={e => setSelectedId(e.target.value)}
                        className="w-full text-sm border-[1px] dark:border-gray-600 rounded-lg px-3 py-2 bg-[#ffffff] dark:bg-[#181d33] dark:text-white"
                    >
                        {recentResponses.map(r => (
                            <option key={r._id} value={r._id}>
                                Submission {r._id.slice(-6)} — {new Date(r.submittedAt).toLocaleDateString()}
                            </option>
                        ))}
                    </select>
                    {!recentResponses.length && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">No submissions yet — submit the form first to test.</p>
                    )}
                    <button
                        onClick={() => onTest(selectedId)}
                        disabled={!selectedId || loading}
                        className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-sm"
                    >
                        {loading ? "Running…" : "Run Dry-Run Test"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TestResultModal({ result, onClose }) {
    const { run, steps } = result;
    const STATUS_COLOR = {
        succeeded: "text-green-600 dark:text-green-400",
        failed: "text-red-500 dark:text-red-400",
        skipped: "text-gray-500 dark:text-gray-400",
        running: "text-blue-500 animate-pulse"
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-[#ffffff] dark:bg-[#23263a] rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        <span className={`font-bold text-sm ${STATUS_COLOR[run.status] || "text-gray-700"}`}>
                            Test {run.status === "succeeded" ? "Passed ✓" : run.status === "failed" ? "Failed ✗" : run.status}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">({run.dryRun ? "dry run" : "live"})</span>
                    </div>
                    <button onClick={onClose}><Icon name="x" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                    {steps.map(s => (
                        <div key={s._id} className="border-[1px] dark:border-gray-700 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{formatStepIndex(s.stepIndex)}</span>
                                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{s.stepName || s.stepType}</span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{s.stepType}</span>
                                </div>
                                <span className={`text-xs font-bold ${STATUS_COLOR[s.status] || ""}`}>{s.status}</span>
                            </div>
                            {s.status === "failed" && s.error && (
                                <p className="text-xs text-red-500 dark:text-red-400 font-mono bg-red-50 dark:bg-red-900/20 rounded p-2">{s.error}</p>
                            )}
                            {s.output && Object.keys(s.output).length > 0 && (
                                <pre className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-[#181d33] rounded p-2 overflow-x-auto mt-1">
                                    {JSON.stringify(s.output, null, 2)}
                                </pre>
                            )}
                            {s.costCents > 0 && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">${(s.costCents / 100).toFixed(4)} · {s.latencyMs}ms</p>
                            )}
                        </div>
                    ))}
                </div>
                <div className="px-5 py-3 border-t dark:border-gray-700 flex justify-between items-center">
                    <span className="text-xs text-gray-500">Total cost: ${((run.totalCostCents || 0) / 100).toFixed(4)}</span>
                    <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold">Close</button>
                </div>
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function WorkflowBuilder() {
    const { formId, workflowId } = useParams();
    const navigate = useNavigate();
    const { listWorkflows, createWorkflow, updateWorkflow, testWorkflow, listStepTypes, listRecipes, installRecipe, loading, error } = useWorkflows();
    const { getForm, getResponses } = useFormBuilder();

    const isNew = workflowId === "new";
    const [form, setForm] = useState(null);
    const [workflow, setWorkflow] = useState({
        name: "My Workflow",
        enabled: true,
        trigger: "on_submit",
        triggerConfig: {},
        steps: []
    });
    const [stepTypes, setStepTypes] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [recentResponses, setRecentResponses] = useState([]);
    const [selectedPath, setSelectedPath] = useState(null);   // e.g. [2] or [2,"then",0]
    const [showRecipes, setShowRecipes] = useState(false);
    const [showTestPicker, setShowTestPicker] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [costData, setCostData] = useState(null); // eslint-disable-line no-unused-vars

    const load = useCallback(async () => {
        const [formData, typesData, recipesData, responsesData] = await Promise.all([
            getForm(formId),
            listStepTypes(),
            listRecipes(),
            getResponses(formId, 1)
        ]);
        if (formData) setForm(formData);
        if (typesData) setStepTypes(typesData);
        if (recipesData) setRecipes(recipesData);
        if (responsesData) setRecentResponses(responsesData.responses || []);

        if (!isNew) {
            const wf = await listWorkflows(formId);
            const found = wf?.find(w => w._id === workflowId);
            if (found) setWorkflow(found);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId, workflowId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => { if (error) toast.error(error); }, [error]);

    const stepMetaMap = Object.fromEntries(stepTypes.map(s => [s.type, s]));

    // This form's own fields → real template variables (responses are keyed by field.name).
    // Plus the always-present computed `score`. Steps/analysis vars are added in the panel.
    const formVars = useMemo(() => {
        const vars = (form?.fields || [])
            .filter(f => f.name && !f.isHidden)
            .map(f => ({ token: `{{ submission.answers.${f.name} }}`, label: f.label || f.name, name: f.name }));
        vars.push({ token: "{{ submission.answers.score }}", label: "Total score" });
        return vars;
    }, [form]);

    const selectedStep = selectedPath ? getStepAt(workflow.steps, selectedPath) : null;

    // Steps that run before the selected one — including any If / Decide it sits
    // inside — with the outputs each publishes. This is what makes the "what to
    // classify" / "value to check" pickers concrete instead of a raw path box.
    const priorSteps = useMemo(
        () => (selectedPath ? priorStepsAlongPath(workflow.steps, selectedPath, stepMetaMap) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [workflow.steps, selectedPath, stepTypes]
    );

    // listPath: [] for the main list, [i, "then"] for a branch lane.
    const addStep = (listPath, type) => {
        const meta = stepMetaMap[type];
        const newStep = {
            stepId: Math.random().toString(36).slice(2, 10),
            type,
            name: meta?.label || type,
            config: {},
            stopOnFailure: true,
            retryCount: 2
        };
        setWorkflow(w => ({ ...w, steps: appendStepAt(w.steps, listPath, newStep) }));
        setSelectedPath([...listPath, listLengthAt(workflow.steps, listPath)]);
    };

    const updateStep = (path, updated) => {
        setWorkflow(w => ({ ...w, steps: replaceStepAt(w.steps, path, updated) }));
    };

    const deleteStep = (path) => {
        setWorkflow(w => ({ ...w, steps: removeStepAt(w.steps, path) }));
        setSelectedPath(p => {
            if (!p) return p;
            // The deleted step, or anything nested inside it, loses its panel.
            if (samePath(p.slice(0, path.length), path)) return null;
            // A later sibling in the same list shifts up by one.
            const parent = path.slice(0, -1);
            const removedIdx = path[path.length - 1];
            if (samePath(p.slice(0, parent.length), parent) && p.length > parent.length && p[parent.length] > removedIdx) {
                const next = [...p];
                next[parent.length] -= 1;
                return next;
            }
            return p;
        });
    };

    // Old-style If steps skipped the next N steps when the check failed — meaning
    // those N steps were really the "if yes" case. Move them into the lane.
    const convertLegacyIf = (path) => {
        if (path.length !== 1) return;
        const idx = path[0];
        setWorkflow(w => {
            const steps = [...w.steps];
            const step = steps[idx];
            if (!step) return w;
            const n = Math.max(1, Number(step.config?.skipSteps) || 1);
            const moved = steps.splice(idx + 1, n);
            const { skipSteps, ...config } = step.config || {};   // eslint-disable-line no-unused-vars
            steps[idx] = {
                ...step,
                config,
                branches: { ...(step.branches || {}), then: [...(step.branches?.then || []), ...moved] }
            };
            return { ...w, steps };
        });
        toast("Switched to IF YES / IF NO — check the lanes");
    };

    const save = async () => {
        setSaving(true);
        let result;
        if (isNew) {
            result = await createWorkflow(formId, workflow);
        } else {
            result = await updateWorkflow(workflowId, workflow);
        }
        setSaving(false);
        if (result) {
            toast.success("Workflow saved!");
            if (isNew) navigate(`/form-builder/${formId}/workflows/${result._id}`);
            else setWorkflow(result);
        }
    };

    const handleTest = async (submissionId) => {
        setShowTestPicker(false);
        const id = workflow._id || workflowId;
        if (!id || isNew) { toast.error("Save the workflow first"); return; }
        const result = await testWorkflow(id, submissionId);
        if (result) setTestResult(result);
    };

    const handleInstallRecipe = async (recipeId) => {
        const result = await installRecipe(formId, recipeId);
        if (result) {
            toast.success("Recipe installed!");
            setShowRecipes(false);
            setWorkflow(result);
            navigate(`/form-builder/${formId}/workflows/${result._id}`);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#181d33] font-Rajdhani dark:text-white">
            {/* Topbar */}
            <div className="border-b dark:border-gray-700 bg-[#ffffff] dark:bg-[#181d33] sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link
                            to={`/form-builder/${formId}/workflows`}
                            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex-shrink-0 no-underline"
                        >
                            ← Workflows
                        </Link>
                        <input
                            value={workflow.name}
                            onChange={e => setWorkflow(w => ({ ...w, name: e.target.value }))}
                            className="text-base font-bold bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-indigo-500 outline-none px-1 max-w-xs"
                        />
                        <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
                            <div
                                onClick={() => setWorkflow(w => ({ ...w, enabled: !w.enabled }))}
                                className={`w-8 h-4 rounded-full transition-colors ${workflow.enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"}`}
                            >
                                <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${workflow.enabled ? "translate-x-4" : "translate-x-0"}`} />
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{workflow.enabled ? "On" : "Off"}</span>
                        </label>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => setShowRecipes(true)}
                            className="px-3 py-1.5 text-xs rounded-lg border-[1px] dark:border-gray-600 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            Recipes
                        </button>
                        <button
                            onClick={() => setShowTestPicker(true)}
                            disabled={isNew || workflow.steps.length === 0}
                            className="px-3 py-1.5 text-xs rounded-lg border-[1px] border-indigo-400 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400 font-semibold hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40"
                        >
                            Test
                        </button>
                        <button
                            onClick={save}
                            disabled={saving || loading}
                            className="px-4 py-1.5 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-6 flex gap-5 pb-12">
                {/* Left: workflow canvas */}
                <div className="flex-1 min-w-0">
                    {/* Trigger */}
                    <div className="mb-3">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Trigger</p>
                        <div className="border-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50  rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                                <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">Form Trigger</span>
                            </div>
                            <select
                                value={workflow.trigger}
                                onChange={e => setWorkflow(w => ({ ...w, trigger: e.target.value }))}
                                className="w-full text-sm border-[1px] dark:border-gray-600 rounded-lg px-3 py-2 bg-[#ffffff] dark:bg-[#181d33] dark:text-white"
                            >
                                {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                            {workflow.trigger === "on_submit" && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                                    Runs before AI analysis — <code className="font-mono">{"{{ analysis.* }}"}</code> variables won't be filled yet. To use lead score / sentiment / summary, choose <strong>After AI analysis</strong>.
                                </p>
                            )}
                            {workflow.trigger === "on_high_score" && (
                                <div className="mt-2 flex items-center gap-2">
                                    <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Min lead score:</label>
                                    <input
                                        type="number" min="0" max="100"
                                        value={workflow.triggerConfig?.minScore ?? 70}
                                        onChange={e => setWorkflow(w => ({ ...w, triggerConfig: { ...w.triggerConfig, minScore: Number(e.target.value) } }))}
                                        className="w-20 text-sm border-[1px] dark:border-gray-600 rounded px-2 py-1 bg-[#ffffff] dark:bg-[#181d33] dark:text-white"
                                    />
                                </div>
                            )}
                            {workflow.trigger === "on_keyword" && (
                                <div className="mt-2 flex items-center gap-2">
                                    <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Keyword:</label>
                                    <input
                                        type="text"
                                        placeholder="urgent"
                                        value={workflow.triggerConfig?.keyword || ""}
                                        onChange={e => setWorkflow(w => ({ ...w, triggerConfig: { ...w.triggerConfig, keyword: e.target.value } }))}
                                        className="flex-1 text-sm border-[1px] dark:border-gray-600 rounded px-2 py-1 bg-[#ffffff] dark:bg-[#181d33] dark:text-white"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Steps */}
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Steps</p>
                    <StepList
                        steps={workflow.steps}
                        listPath={[]}
                        stepTypes={stepTypes}
                        stepMeta={stepMetaMap}
                        selectedPath={selectedPath}
                        onSelect={setSelectedPath}
                        onDelete={deleteStep}
                        onAdd={addStep}
                    />

                    {workflow.steps.length === 0 && (
                        <div className="mt-6 text-center py-12 border-2 border-dashed dark:border-gray-700 rounded-xl text-gray-500 dark:text-gray-400">
                            <Icon name="zap" className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            <p className="font-semibold">No steps yet</p>
                            <p className="text-sm mt-1">Click "Add Step" or install a recipe to get started</p>
                        </div>
                    )}

                    {/* Run History link */}
                    {!isNew && (
                        <div className="mt-6 flex items-center justify-between text-sm">
                            <Link
                                to={`/form-builder/${formId}/workflows/${workflowId}/runs`}
                                className="text-indigo-600 dark:text-indigo-400 hover:underline no-underline font-medium"
                            >
                                View Run History →
                            </Link>
                            {costData && (
                                <span className="text-gray-500 dark:text-gray-400">
                                    This month: {costData.totalRuns} runs · ${((costData.totalCostCents || 0) / 100).toFixed(3)}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Right: step config panel */}
                {selectedStep && (
                    <div className="w-80 flex-shrink-0 border-[1px] dark:border-gray-700 rounded-xl bg-[#ffffff] dark:bg-[#23263a] overflow-hidden self-start sticky top-20">
                        <StepConfigPanel
                            key={selectedStep.stepId || selectedPath.join("-")}
                            step={selectedStep}
                            stepMeta={stepMetaMap}
                            allStepTypes={stepTypes}
                            formVars={formVars}
                            priorSteps={priorSteps}
                            canConvertLegacyIf={selectedPath.length === 1 && !selectedStep.branches?.then?.length}
                            onConvertLegacyIf={() => convertLegacyIf(selectedPath)}
                            onUpdate={updated => updateStep(selectedPath, updated)}
                            onClose={() => setSelectedPath(null)}
                        />
                    </div>
                )}
            </div>

            {/* Modals */}
            {showRecipes && <RecipeModal recipes={recipes} onInstall={handleInstallRecipe} onClose={() => setShowRecipes(false)} />}
            {showTestPicker && (
                <TestModal runs={recentResponses} onTest={handleTest} onClose={() => setShowTestPicker(false)} loading={loading} />
            )}
            {testResult && <TestResultModal result={testResult} onClose={() => setTestResult(null)} />}

            <ToastContainer position="top-right" autoClose={2000}
                theme={localStorage.getItem("theme") === "dark" ? "dark" : "light"} />
        </div>
    );
}
